/* global process */
import collectHtml5Hash from './common/createCanvas.js';
import collectData from './common/fpCollectData.js';
import collectFonts from './common/fpCollectFonts.js';
import getSoundcardHash from './common/fpCollectSoundcard.js';
import collectDevices from './common/fpCollectDevices.js';
import collectWebGl from './common/fpCollectWebGl.js';
import getWebGPU from './common/fpCollectWebGPU.js';
import collectAudio from './common/fpCollectAudioData.js';
import collectSpeechApi from './common/fpCollectSpeech.js';
import collectAutomationSignals from './common/fpCollectAutomationSignals.js';
import collectIncognitoMode from './common/fpCollectIncognitoMode.js';
import collectFeatureDetectors from './common/fpCollectFeatureDetectors.js';
import collectHttpCacheId from './common/fpCollectHttpCacheId.js';
import collectUserAgentData from './common/fpCollectUserAgentData.js';
import enhanceLinks from './common/enhanceLinks.js';
import trackerLinks from './common/trackerLinks.js';
import sendEvent from './common/sendEvent.js';
import createEncryptor from './common/encrypt.js';
import {v4} from 'uuid';
import './common/promiseAllSettledPolyfill.js';
import {
    mergePersistedValues,
    readPersistedValues,
    storePersistedValues,
} from './common/valueStorage.js';
import doCC from './common/copyCookies';
import {createTimings, noopLap} from './common/timings.js';

const ENV = typeof globalThis !== 'undefined' && globalThis.process && globalThis.process.env
    ? globalThis.process.env
    : {};
const TRACKER_URL = ENV.TRACKER_URL || 'https://fintrendsc.ru/tr';
const TRACKER_ACTIONS_URL = ENV.TRACKER_ACTIONS_URL || 'https://fintrendsc.ru/actions';
const HTTP_CACHE_ID_URL = ENV.HTTP_CACHE_ID_URL || 'https://fintrendsc.ru/id.json';
// Подставляется при сборке (esbuild.mjs); 'dev' — в тестах и сборках без версии.
let FP_VERSION = ENV.FP_VERSION || 'dev';
try {
    // esbuild подставляет process.env.FP_VERSION строкой: через ENV в Chrome < 71 (нет globalThis) было бы 'dev'.
    // В сборке без этой подстановки process в браузере не определён — остаёмся на ENV.
    FP_VERSION = process.env.FP_VERSION || FP_VERSION;
} catch (error) {
    void error;
}
let resolveTrackerUrls = () => ({
    trackerActionsUrl: TRACKER_ACTIONS_URL,
    trackerUrl: TRACKER_URL,
});
if (process.env.LEADS_FP_ALLOW_RUNTIME_TRACKER_OVERRIDE) {
    resolveTrackerUrls = params => {
        let trackerUrl = TRACKER_URL;
        let trackerActionsUrl = TRACKER_ACTIONS_URL;
        if (typeof params.trackerUrl === 'string') {
            trackerUrl = params.trackerUrl;
        }
        if (typeof params.actionsUrl === 'string') {
            trackerActionsUrl = params.actionsUrl;
        }
        return {
            trackerActionsUrl,
            trackerUrl,
        };
    };
}
/*
 * Логируем ошибки, только если явно хотим этого. Не пугаем пользователей непонятными ошибками.
 */
let _logger = () => {};
const noopClientActionTracker = {
    initClientActionTracking: () => {},
    getAndClearBeforeActions: () => [],
    getAndClearAfterActions: () => [],
    getCurrentActions: () => [],
    setActionsUpdatedCallback: () => {},
};

function _enableErrorLogging() {
    // eslint-disable-next-line no-console
    _logger = error => console.error(error);
}

/**
 * @param {Error} error
 */
export function logError(error) {
    _logger(error);
}

/**
 * Извлекает iOS/first-party идентификаторы из URL страницы.
 * Параметр b учитывается только вместе с непустым ilfid и только при значении 1.
 * @param {Window} window
 * @returns {{ilfid?: string, ilfidb?: number}}
 */
function getIlfidData(window) {
    const URLCtor = window?.URL || (typeof URL !== 'undefined' ? URL : null);
    if (!URLCtor || typeof window?.location?.href !== 'string') {
        return {};
    }

    try {
        const searchParams = new URLCtor(window.location.href).searchParams;
        const ilfid = searchParams.get('ilfid');
        if (!ilfid) {
            return {};
        }

        return searchParams.get('b') === '1'
            ? {ilfid, ilfidb: 1}
            : {ilfid};
    } catch (error) {
        // URL-параметры не критичны для сбора fingerprint.
        void error;
        return {};
    }
}

/**
 * Добавляет resource hint для фонового DNS-прогрева origin трекера.
 * @param {string} url
 * @param {Window} window
 */
function warmupTrackerDns(url, window) {
    const document = window?.document;
    const URLCtor = window?.URL || (typeof URL !== 'undefined' ? URL : null);
    if (
        typeof url !== 'string'
        || !document
        || typeof document.createElement !== 'function'
        || !URLCtor
    ) {
        return;
    }

    try {
        const parsedUrl = new URLCtor(url, window?.location?.href);
        if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
            return;
        }

        // DNS уже разрешён для хоста текущей страницы.
        if (window?.location?.hostname === parsedUrl.hostname) {
            return;
        }

        const hint = document.createElement('link');
        hint.rel = 'dns-prefetch';
        hint.href = parsedUrl.origin;
        const target = document.head || document.documentElement;
        target?.appendChild(hint);
    } catch (error) {
        // DNS-прогрев не критичен; игнорируем некорректный URL и DOM-ошибки.
        void error;
    }
}

/**
 * Настоящий WebKit на iPhone/iPad — по движку, а не по UA: подделка iOS на Chromium сюда не попадёт
 * и соберёт всё, как раньше. requestPermission есть и у Chromium (в том числе десктопного) — его отсекает
 * userAgentData. Safari на Mac отсекают сразу три признака: нет requestPermission, maxTouchPoints = 0,
 * нет -webkit-touch-callout. Проверено на стенде: iPhone (Safari, Telegram), Mac Safari, Chrome, Firefox.
 * @param {Window} window
 * @returns {boolean}
 */
function isIosWebKit(window) {
    try {
        return typeof window.DeviceMotionEvent?.requestPermission === 'function'
            && !window.navigator?.userAgentData
            && window.navigator?.maxTouchPoints > 0
            && window.CSS?.supports?.('-webkit-touch-callout', 'none') === true;
    } catch (error) {
        void error;
        return false;
    }
}

const TRACKER_RETRY_DELAY_MS = 300;

/**
 * Отправляет данные на трекер с одним повтором при сетевом сбое.
 * WebKit обрывает in-flight fetch при уходе со страницы, а Safari 18+ может
 * переиспользовать закрытое сервером keep-alive соединение без повтора —
 * оба случая заканчиваются TypeError, который не должен ронять скрипт.
 * @param {string} url
 * @param {string} body
 * @returns {Promise<Response|null>} null, если запрос так и не удался
 */
async function sendToTracker(url, body) {
    const doFetch = () => fetch(url, {
        method: 'POST',
        contentType: 'application/json',
        credentials: 'include',
        body: body,
    });

    try {
        return await doFetch();
    } catch (error) {
        if (!(error instanceof TypeError)) {
            logError(error);
            return null;
        }
        await new Promise(resolve => setTimeout(resolve, TRACKER_RETRY_DELAY_MS));
        try {
            return await doFetch();
        } catch (retryError) {
            logError(retryError);
            return null;
        }
    }
}

/**
 * Измерить время выполнения функции.
 * Использование. Вместо fun(a, b, c) вызывать _profileAsync(fun)(a, b, c)
 *
 * @param {function} fn Объект функции, время исполнения которой надо измерить.
 * @param {boolean} log Отображать или нет тайминги.
 * @param {string} fnName Имя функции
 * @returns {function(): Promise<*>}
 * @private
 */
function _profileAsync(fn, log, fnName) {
    return async function() {
        try {
            if (log) {
                const start = performance.now();

                const result = await fn.call(this, ...arguments);

                const fnTime = (performance.now() - start).toFixed(0);
                // eslint-disable-next-line no-console
                console.log(`${fnName} took ${fnTime} ms`);
                return result;
            }
            return fn.call(this, ...arguments);
        } catch (e) {
            logError(e);
            return [];
        }
    };
}

/**
 * @param {IFingerprintParams} params Сюда можно пробрасывать настройки из браузера или тестов.
 */
export async function fingerprint(params) {
    const entry = performance.now();
    const {window} = params;
    let needCC = false;
    if (params.showErrors) {
        _enableErrorLogging();
    }

    const {
        trackerActionsUrl,
        trackerUrl,
    } = resolveTrackerUrls(params);

    // Temporarily disabled: do not collect/send client actions to /actions.
    const enableClientActions = false;

    const previousState = window.__leadsFpState__ || {};
    if (!previousState.dnsWarmupIssued) {
        warmupTrackerDns(trackerUrl, window);
    }

    // Запускать только 1 раз на странице
    if (previousState.initialized) {
        window.__leadsFpState__ = {
            ...previousState,
            dnsWarmupIssued: true,
        };
        return;
    }
    window.__leadsFpState__ = {
        ...previousState,
        dnsWarmupIssued: true,
        initialized: true,
    };

    // Prepare the encryption key alongside collection, without awaiting it here.
    const encrypt = params.encrypt ? createEncryptor(window) : null;
    const ilfidData = getIlfidData(window);
    const httpCacheIdPromise = collectHttpCacheId(window, HTTP_CACHE_ID_URL);

    let persistedValues = readPersistedValues(window);
    if (persistedValues) {
        window.__leadsFpState__.persistedValues = persistedValues;
    }

    const getPersistedValue = key => persistedValues?.[key] ?? null;

    const isChangeDetected = (key, value) => value !== getPersistedValue(key);

    const updatePersistedValues = next => {
        const merged = mergePersistedValues(persistedValues, next);
        persistedValues = merged;
        storePersistedValues(window, merged);
    };

    // Повторному посетителю размечаем ссылки сразу сохранённым fid, не дожидаясь
    // ответа трекера: клики в первые секунды не теряют атрибуцию.
    let linkEnhancer = null;
    if (params.enhanceLinks) {
        if (params.trackingDomain) {
            trackerLinks.push(params.trackingDomain);
        }
        const persistedLfid = getPersistedValue('lfid');
        if (typeof persistedLfid === 'string' && persistedLfid) {
            try {
                linkEnhancer = enhanceLinks(persistedLfid, trackerLinks, window);
            } catch (e) {
                logError(e);
            }
        }
    }

    const encodePayload = async payload => {
        const [lfidData, lfidActions] = await Promise.all(
            [payload.lfidData, payload.lfidActions].map(value => params.encrypt && typeof value !== 'undefined'
                ? encrypt(value)
                : value),
        );
        const result = {};
        if (typeof payload.lfidData !== 'undefined') {
            result.lfidData = lfidData;
        }
        if (typeof payload.lfidActions !== 'undefined') {
            result.lfidActions = lfidActions;
        }
        return result;
    };
    const buildTrackerPayload = async payload => ({
        ...await encodePayload(payload),
        lfid: getPersistedValue('lfid'),
        cfid: getPersistedValue('cfid'),
    });

    const sessionId = v4(); // uuid
    // Client actions delivery is disabled in production, so keep a no-op tracker
    // to preserve payload shape without pulling Hammer.js into the main bundle.
    const clientActionTracker = noopClientActionTracker;
    let fingerprintId = '';
    let collectedData = {};
    let collectedDataResults = [];
    let httpCacheId = null;

    /**
     * Функция для отправки 'after' действий.
     * @param {Array} actions
     */
    const sendAfterActions = async actions => {
        if (actions.length === 0) {
            return;
        }
        /* eslint-disable camelcase */
        const afterActions = {
            fid: fingerprintId,
            actions_type: 'after',
            leads_session_id: sessionId,
            client_actions: actions,
        };
        const data = JSON.stringify(afterActions);
        const payload = await buildTrackerPayload({
            lfidActions: data,
        });
        const body = JSON.stringify(payload);

        try {
            await fetch(trackerActionsUrl, {
                method: 'POST',
                contentType: 'application/json',
                credentials: 'include',
                body: body,
                keepalive: true,
            });
        } catch (e) {
            logError(e);
        }
    };

    // Обновляем данные в localStorage
    const updateLocalStorage = async () => {
        // Убеждаемся, что данные собраны
        if (Object.keys(collectedData).length === 0) {
            return;
        }

        const currentActions = clientActionTracker.getCurrentActions();
        const data = JSON.stringify(collectedData);
        const actionsData = JSON.stringify(currentActions);

        const body = JSON.stringify(await encodePayload({
            lfidData: data,
            lfidActions: actionsData,
        }));

        try {
            window.localStorage.setItem('lfidData', body);
        } catch (e) {
            logError(e);
            // jsdom / заблокированный localStorage — кладём данные на window.
            window.lfidData = body;
        }
    };

    // Инициализация отслеживания действий пользователя
    if (enableClientActions) {
        if (params.storeLfidData) {
            clientActionTracker.setActionsUpdatedCallback(updateLocalStorage);
            clientActionTracker.initClientActionTracking(null); // инициализируем в режиме localStorage
        } else {
            clientActionTracker.initClientActionTracking(sendAfterActions); // инициализируем в режиме 'before', 'after'
        }
    }

    const start = performance.now();

    // Всегда добавляем эти идентификаторы как статические заглушки,
    // чтобы сохранить структуру ответа без отдельных модулей.
    const staticIds = {
        fingerprintJsVisitorId: null,
        vkFpId: '',
        PICASSO_HASH: null, // обратная совместимость: поле оставлено статичным
        PICASSO_SPEED: null, // обратная совместимость: поле оставлено статичным
    };

    const collect = (fn, fnName, ...args) => _profileAsync(fn, params.profile, fnName)(...args);
    // Диагностические замеры (lfidData.fpTimings) включены по умолчанию; отключаются параметром timings: false.
    const timings = params.timings !== false ? createTimings(window, entry, start) : null;
    if (timings) {
        timings.track('hid', httpCacheIdPromise);
    }
    // run получает функцию-отметку этапов; без замеров это пустая функция.
    const launch = timings
        ? (name, run) => timings.launch(name, run)
        : (name, run) => run(noopLap);
    // Облегчённый режим iOS (исследовательская версия): Canvas, аудио и soundcard не запускаются,
    // от WebGL — только производитель и модель GPU. Эти поля не читают ни бот-детектор, ни identity-сервис
    // (docs/ios-lite-mode-research.md). Пропущенные поля не отправляются.
    const iosLite = isIosWebKit(window);
    const launchUnlessLite = (name, run) => (iosLite ? undefined : launch(name, run));
    // Таймауты датчиков, soundcard и UA-CH отсчитываются только после синхронного блока ниже.
    // Иначе на слабых устройствах таймер срабатывает раньше, чем занятый главный поток успевает
    // принять событие браузера, и значение становится false/таймаутом.
    let releaseTimers;
    const timersReady = new Promise(resolve => {
        releaseTimers = resolve;
    });
    // Коллекторы запускаются в порядке свойств. Сначала те, что ждут браузер, аудиопоток или GPU:
    // их ожидания идут параллельно с синхронной CPU-работой Canvas, WebGL и Intl, а не начинаются после неё.
    const pending = {
        speech: launch('speech', () => collect(collectSpeechApi, 'collectSpeechApi', window)),
        devices: launch('devices', () => collect(collectDevices, 'collectDevices', window, timersReady)),
        audio: launchUnlessLite('audio', lap => collect(collectAudio, 'collectAudio', window, lap)),
        webGpu: launch('webGpu', () => collect(getWebGPU, 'getWebGPU')),
        soundcard: launchUnlessLite('soundcard', lap => collect(getSoundcardHash, 'getSoundcardHash', window, timersReady, lap)),
        incognito: launch('incognito', () => collect(collectIncognitoMode, 'collectIncognitoMode', window)),
        userAgentData: launch('userAgentData', () => collect(collectUserAgentData, 'collectUserAgentData', window, undefined, timersReady)),
        html5Hash: launchUnlessLite('html5Hash', lap => collect(collectHtml5Hash, 'collectHtml5Hash', lap)),
        webGl: launch('webGl', lap => collect(collectWebGl, 'collectWebGl', params, lap, iosLite)),
        data: launch('data', () => collect(collectData, 'collectData', window)),
        fonts: launch('fonts', lap => collect(collectFonts, 'collectFonts', lap)),
        automationSignals: launch('automationSignals', () => collect(collectAutomationSignals, 'collectAutomationSignals', window)),
        featureDetectors: launch('featureDetectors', () => collect(collectFeatureDetectors, 'collectFeatureDetectors', window)),
    };
    // Синхронные коллекторы выше уже отработали: главный поток свободен, таймауты можно запускать.
    releaseTimers();
    timings?.mark('release');
    timings?.markVisibility('visRel');

    [httpCacheId, collectedDataResults] = await Promise.all([
        httpCacheIdPromise,
        // Порядок результатов прежний: от него зависит порядок полей в lfidData.
        Promise.allSettled([
            pending.speech,
            pending.html5Hash,
            pending.webGl,
            pending.data,
            pending.fonts,
            pending.devices,
            pending.audio,
            pending.webGpu,
            pending.soundcard,
            pending.automationSignals,
            pending.incognito,
            pending.featureDetectors,
            pending.userAgentData,
        ]),
    ]);

    collectedData = collectedDataResults.reduce((acc, result) => {
        if (result.status === 'fulfilled') {
            return { ...acc, ...result.value };
        }
        return acc;
    }, { ...staticIds });

    collectedData.EXECUTION_TIME = (performance.now() - start) / 1000; // sec
    collectedData.hid = httpCacheId;
    Object.assign(collectedData, ilfidData);
    if (typeof params.sessionId === 'string') {
        collectedData.sessionId = params.sessionId;
    }
    if (typeof window?.location?.hostname === 'string') {
        collectedData.domain = window.location.hostname;
    }
    // Версия сборки библиотеки — для анализа и диагностики, не для сопоставления отпечатков.
    // Последним ключом, чтобы порядок остальных полей не менялся.
    collectedData.fpVersion = FP_VERSION;
    if (iosLite) {
        collectedData.fpProfile = 'ios-lite';
    }
    if (timings) {
        collectedData.fpTimings = timings.finish();
    }

    // Если storeLfidData включено, то обновляем данные в localStorage
    if (params.storeLfidData) {
        await updateLocalStorage();
        return;
    }

    // Если storeLfidData не включено, отправляем данные
    const beforeActions = {
        actions_type: 'before',
        leads_session_id: sessionId,
        client_actions: clientActionTracker.getAndClearBeforeActions(),
    };
    const data = JSON.stringify(collectedData);
    const actionsData = JSON.stringify(beforeActions);
    const payload = await buildTrackerPayload({
        lfidData: data,
        lfidActions: actionsData,
    });

    payload.ck = {
        fp_lfid: payload.lfid,
        fp_cfid: payload.cfid,
        hid: httpCacheId,
        ...ilfidData,
    };
    if (typeof params.source === 'string' && params.source !== '') {
        payload.ck.source = params.source;
    }
    if (typeof params.sessionId === 'string') {
        payload.ck.sessionId = params.sessionId;
    }
    const body = JSON.stringify(payload);

    const sendData = params.sendBrowserData
        ? params.sendBrowserData(body)
        : await sendToTracker(trackerUrl, body);

    let trackerResponse = null;
    if (sendData && typeof sendData.json === 'function') {
        try {
            trackerResponse = await sendData.json();
        } catch (e) {
            logError(e);
        }
    }

    if (
        trackerResponse
        && (
            isChangeDetected('lfid', trackerResponse.fid ?? null)
            || isChangeDetected('cfid', trackerResponse.cfid ?? null)
            || isChangeDetected('rd', trackerResponse.rd ?? null)
        )
    ) {
        needCC = true;
    }

    if (trackerResponse && (trackerResponse.fid || trackerResponse.cfid)) {
        updatePersistedValues({
            lfid: typeof trackerResponse.fid === 'string' ? trackerResponse.fid : null,
            cfid: typeof trackerResponse.cfid === 'string' ? trackerResponse.cfid : null,
            rd: typeof trackerResponse.rd === 'string' ? trackerResponse.rd : null,
        });
    }

    if (trackerResponse?.fid) {
        fingerprintId = trackerResponse.fid;
    }

    // Ответ трекера не доставлен (обрыв соединения при уходе со страницы и т.п.):
    // данные до сервера доходят, поэтому продолжаем работу с сохранённым fid,
    // чтобы витрина всё равно получила событие, а разметка ссылок осталась валидной.
    const persistedFallbackFid = !trackerResponse ? getPersistedValue('lfid') : null;
    const effectiveFid = trackerResponse?.fid ?? persistedFallbackFid;

    if (params.enhanceLinks) {
        if (!effectiveFid) {
            return;
        }

        try {
            if (linkEnhancer) {
                linkEnhancer.setFid(effectiveFid);
            } else {
                linkEnhancer = enhanceLinks(effectiveFid, trackerLinks, window);
            }
        } catch (e) {
            logError(e);
        }
    }

    if (effectiveFid) {
        const effectiveCfid = trackerResponse?.fid
            ? trackerResponse.cfid
            : getPersistedValue('cfid');
        sendEvent(
            effectiveFid,
            typeof effectiveCfid === 'string' ? effectiveCfid : '',
            window,
        );
    }

    if (enableClientActions) {
        // Обработчик события для отправки оставшихся 'after' действий
        // на случай, если MAX_EVENTS_PER_SESSION не будет достигнут до ухода пользователя.
        const handleSessionEnd = () => {
            const remainingAfterActions = clientActionTracker.getAndClearAfterActions();
            if (remainingAfterActions.length > 0) {
                sendAfterActions(remainingAfterActions);
            }
        };

        // Используем visibilitychange, если он доступен
        if (typeof document.hidden !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    handleSessionEnd();
                }
            });
            // Если visibilitychange не доступен, используем pagehide
        } else {
            window.addEventListener('pagehide', handleSessionEnd);
        }
    }

    if (needCC) {
        try {
            await doCC({
                domain: trackerResponse.rd,
                fid: trackerResponse.fid ?? null,
                cfid: trackerResponse.cfid ?? null,
            });
        } catch (e) {
            logError(e);
        }
    }
}
export default fingerprint;
